import { mapObjIndexed } from 'ramda';
import { selector, selectorFamily } from 'recoil';
import {
  allValid,
  alwaysValid,
  alwaysValidValidationSelector,
  createMultipleFieldValidationSelector,
  validateSchema,
} from '@zurich/common/recoil';
import type {
  MultipleValidationResults,
  ValidationResult,
} from '@zurich/common/types';
import { vsgaStepConfig } from '~/components/stepConfig';
import {
  checkboxSchema,
  damagedFieldSchema,
  imageSchema,
  licensePlateVSGASchema,
  partyEmailSchema,
  partyNameSchema,
  postalCodeSchema,
} from '~/lib/schemas';
import { isPhotoRequired } from '~/lib/types';
import {
  areaOfDamageFieldSelectorFamily,
  confirmationOfCorrectAutoDataSelector,
  confirmationOfCorrectPhotoDataSelector,
  damageDocumentDataFieldSelectorFamily,
  descriptionOfDamageFieldSelectorFamily,
  dryAndCleanCarSelector,
  emailFieldSelector,
  enoughSpaceSelector,
  firstNameSelector,
  lastNameSelector,
  licensePlateFieldSelector,
  photosFieldSelector,
  postalCodeSelector,
  sunnyAndDryWeatherSelector,
  timeBeforeSelector,
  typeOfDamageFieldSelectorFamily,
} from './selectors';

export const firstNameValidationSelector = selector({
  key: '@zurich/vsga/validation/firstName',
  get: ({ get }) => {
    const firstName = get(firstNameSelector);
    return validateSchema(partyNameSchema, firstName);
  },
});

export const lastNameValidationSelector = selector({
  key: '@zurich/vsga/validation/lastName',
  get: ({ get }) => {
    const lastName = get(lastNameSelector);
    return validateSchema(partyNameSchema, lastName);
  },
});

export const postalCodeValidationSelector = selector({
  key: '@zurich/vsga/validation/postalCode',
  get: ({ get }) => {
    const postalCode = get(postalCodeSelector);
    return validateSchema(postalCodeSchema, postalCode);
  },
});

export const emailValidationSelector = selector({
  key: '@zurich/vsga/validation/email',
  get: ({ get }) => {
    const email = get(emailFieldSelector);
    return validateSchema(partyEmailSchema, email);
  },
});

/**
 * @category Validator
 * @return Validators for contact information
 */
export const contactInformationValidationSelector =
  createMultipleFieldValidationSelector(
    '@zurich/vsga/validation/contactInformation',
    {
      firstName: firstNameValidationSelector,
      lastName: lastNameValidationSelector,
      postalCode: postalCodeValidationSelector,
      email: emailValidationSelector,
    }
  );

export const dryAndCleanValidationSelector = selector({
  key: '@zurich/vsga/validation/dryAndClean',
  get: ({ get }) => {
    const dryAndClean = get(dryAndCleanCarSelector);
    return validateSchema(checkboxSchema, dryAndClean);
  },
});

export const sunnyAndDryWeatherValidationSelector = selector({
  key: '@zurich/vsga/validation/sunnyAndDryWeather',
  get: ({ get }) => {
    const sunnyAndDryWeather = get(sunnyAndDryWeatherSelector);
    return validateSchema(checkboxSchema, sunnyAndDryWeather);
  },
});

export const enoughSpaceValidationSelector = selector({
  key: '@zurich/vsga/validation/enoughSpace',
  get: ({ get }) => {
    const enoughSpace = get(enoughSpaceSelector);
    return validateSchema(checkboxSchema, enoughSpace);
  },
});

export const timeBeforeValidationSelector = selector({
  key: '@zurich/vsga/validation/timeBefore',
  get: ({ get }) => {
    const timeBefore = get(timeBeforeSelector);
    return validateSchema(checkboxSchema, timeBefore);
  },
});

/**
 * @category Validator
 * @return Validators for pre-check step
 */
export const preCheckValidationSelector = createMultipleFieldValidationSelector(
  '@zurich/vsga/validation/preCheck',
  {
    dryAndClean: dryAndCleanValidationSelector,
    sunnyAndDryWeather: sunnyAndDryWeatherValidationSelector,
    enoughSpace: enoughSpaceValidationSelector,
    timeBefore: timeBeforeValidationSelector,
  }
);

export const confirmationOfCorrectAutoDataValidationSelector = selector({
  key: '@zurich/vsga/validation/confirmationOfCorrectAutoData',
  get: ({ get }) => {
    const confirmationOfCorrectAutoData = get(
      confirmationOfCorrectAutoDataSelector
    );
    return validateSchema(checkboxSchema, confirmationOfCorrectAutoData);
  },
});

export const confirmationOfCorrectPhotoDataValidationSelector = selector({
  key: '@zurich/vsga/validation/confirmationOfCorrectPhotoData',
  get: ({ get }) => {
    const confirmationOfCorrectPhotoData = get(
      confirmationOfCorrectPhotoDataSelector
    );
    return validateSchema(checkboxSchema, confirmationOfCorrectPhotoData);
  },
});

export const licensePlateValidationSelector = selector({
  key: '@zurich/vsga/validation/licensePlateValidationSelector',
  get: ({ get }) => {
    const licensePlate = get(licensePlateFieldSelector);
    return validateSchema(licensePlateVSGASchema, licensePlate);
  },
});

/**
 * `imageValidationSelectorFamily` is used to validate any steps with photos.
 *
 * To add a new validation, update the `isPhotoRequired` function.
 */
export const imageValidationSelectorFamily = selectorFamily<
  ValidationResult,
  string
>({
  key: '@zurich/vsga/validation/imageValidationSelectorFamily',
  get:
    (step) =>
    ({ get }) => {
      if (!isPhotoRequired(step)) {
        return alwaysValid(true);
      }
      const photoUploads = get(photosFieldSelector);

      const currentPhotoUpload = photoUploads.find(
        (upload) => upload.id === step
      );
      return validateSchema(
        imageSchema,
        currentPhotoUpload?.documentId ?? undefined
      );
    },
});

export const serialNumberPhotoValidationSelector =
  imageValidationSelectorFamily('serialnumber');

export const kilometerPhotoValidationSelector =
  imageValidationSelectorFamily('kilometers');

export const frontPhotoValidationSelector =
  imageValidationSelectorFamily('front');

export const windshieldPhotoValidationSelector =
  imageValidationSelectorFamily('windshield');

export const driversidePhotoValidationSelector =
  imageValidationSelectorFamily('driverside');

export const roofLeftPhotoValidationSelector =
  imageValidationSelectorFamily('roofLeft');

export const roofRightPhotoValidationSelector =
  imageValidationSelectorFamily('roofRight');

export const rearPhotoValidationSelector =
  imageValidationSelectorFamily('rear');

export const passengersidePhotoValidationSelector =
  imageValidationSelectorFamily('passengerside');

export const frontDriversideAnglePhotoValidationSelector =
  imageValidationSelectorFamily('frontDriversideAngle');

export const frontPassengersideAnglePhotoValidationSelector =
  imageValidationSelectorFamily('frontPassengersideAngle');

export const backDriversideAnglePhotoValidationSelector =
  imageValidationSelectorFamily('backDriversideAngle');

export const backPassengersideAnglePhotoValidationSelector =
  imageValidationSelectorFamily('backPassengersideAngle');

export const damagedPartValidationSelectorFamily = selectorFamily<
  ValidationResult,
  string
>({
  key: '@zurich/vsga/validation/damagedPartValidationSelectorFamily',
  get:
    (id) =>
    ({ get }) => {
      const damagedPart = get(
        areaOfDamageFieldSelectorFamily({
          id,
        })
      );
      return validateSchema(damagedFieldSchema, damagedPart);
    },
});

export const damagedTypeValidationSelectorFamily = selectorFamily<
  ValidationResult,
  string
>({
  key: '@zurich/vsga/validation/damagedTypeValidationSelectorFamily',
  get:
    (id) =>
    ({ get }) => {
      const damagedType = get(
        typeOfDamageFieldSelectorFamily({
          id,
        })
      );
      return validateSchema(damagedFieldSchema, damagedType);
    },
});

export const damageDesciptionValidationSelectorFamily = selectorFamily<
  ValidationResult,
  string
>({
  key: '@zurich/vsga/validation/damageDesciptionValidationSelectorFamily',
  get:
    (id) =>
    ({ get }) => {
      const damageDesciption = get(
        descriptionOfDamageFieldSelectorFamily({
          id,
        })
      );
      return validateSchema(damagedFieldSchema, damageDesciption);
    },
});

export const damagePhotoValidationSelectorFamily = selectorFamily<
  ValidationResult,
  string
>({
  key: '@zurich/vsga/validation/damagePhotoValidationSelectorFamily',
  get:
    (id) =>
    ({ get }) => {
      const damagePhoto = get(
        descriptionOfDamageFieldSelectorFamily({
          id,
        })
      );
      return validateSchema(damagedFieldSchema, damagePhoto);
    },
});

export const damageDocumentValidationSelectorFamily = selectorFamily<
  ValidationResult,
  string
>({
  key: '@zurich/vsga/validation/damagePhotoValidationSelectorFamily',
  get:
    (id) =>
    ({ get }) => {
      const damageDocument = get(
        damageDocumentDataFieldSelectorFamily({
          id,
        })
      );
      return validateSchema(damagedFieldSchema, damageDocument);
    },
});

export const damageFieldsValidationSelectorFamily = selectorFamily<
  MultipleValidationResults,
  string
>({
  key: '@zurich/vsga/validation/damageFieldsValidationSelectors',
  get:
    (id) =>
    ({ get }) => {
      return allValid(
        mapObjIndexed(
          (validator) => get(validator(id)),
          damageValidationSelectors
        )
      );
    },
});

export const damageValidationSelectors = {
  damageDescription: damageDesciptionValidationSelectorFamily,
  damageType: damagedTypeValidationSelectorFamily,
  vehiclePart: damagedPartValidationSelectorFamily,
  image: damageDocumentValidationSelectorFamily,
};

export const payloadDataValidByStepValidationSelectorFamily = selectorFamily<
  number,
  { activeStep: number }
>({
  key: '@zurich/vsga/validation/payloadByStepValidationSelectorFamily',
  get:
    ({ activeStep }) =>
    ({ get }) => {
      const allValidations = vsgaStepConfig
        .slice(0, activeStep + 1)
        .map(({ validationSelector }) =>
          get(validationSelector || alwaysValidValidationSelector)
        );

      const invalidStep = allValidations.findIndex(
        (validation) => validation.valid === false
      );

      return invalidStep;
    },
});
